import { observer } from 'mobx-react'
import { useEffect, useState } from 'react'
import { useStore } from '../../../../../hooks/useStore'
import { useIntl } from 'react-intl'
import { useHistory } from 'react-router-dom'
import { Elements, TextButton, utils, Loader } from '@tfo/common'
import { IconButton } from '@material-ui/core'
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos'
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos'
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked'
import RadioButtonCheckedIcon from '@material-ui/icons/RadioButtonChecked'
import tfoLogo from '../../../../../media/images/tfo-magnifying-glass.png'
import $http from '../../../../../utils/$http'
import './NewCustomer.scss'
import CreatePasswordFromGuide from './create-password-from-guide/CreatePasswordFromGuide'
import { IntlProviderWrapper } from '../../../../../IntlProviderWrapper'
import LZstring from 'lz-string'

const { Heading1, Paragraph, InputErrorMessage } = Elements

enum Steps {
	Welcome = 0,
	Info,
	Auth,
	Approval
}

interface INewCustomerToken {
	token: string
}

export const NewCustomer = observer(() => {
	const [activeStep, setActiveStep] = useState(Steps.Welcome)
	const [showErrorMessage, setShowErrorMessage] = useState(false)
	const [password, setPassword] = useState('')
	const [customerLanguage, setCustomerLanguage] = useState('English')

	const rootStore = useStore()
	const history = useHistory()

	useEffect(() => {
		rootStore.clientViewStore.toggleExternalHostedOrderFlow(true)
		const params = new URLSearchParams(document.location.search || window.location.search)
		const customerInfoAsBase64 = params.get('cust')
		const cust = LZstring.decompressFromBase64(customerInfoAsBase64)
		const custObject = JSON.parse(cust)
		setCustomerLanguage(custObject.Language)
	}, [])

	const handlePasswordChange = (password: string) => {
		setPassword(password)
	}

	const handleNext = () => {
		setActiveStep(prevActiveStep => prevActiveStep + 1)
	}

	const handleBack = () => {
		setActiveStep(prevActiveStep => prevActiveStep - 1)
	}

	const createUserAndLogin = async () => {
		rootStore.clientViewStore.loading = true
		const params = new URLSearchParams(document.location.search || window.location.search)
		const customerInfoAsBase64 = params.get('cust')
		const individualInfoAsBase64 = params.get('body')
		const encodedCustomer = encodeURIComponent(customerInfoAsBase64)
		const authToken = params.get('auth')
		const token = { token: authToken }

		utils.setCookie(JSON.stringify(token))

		$http
			.post<INewCustomerToken>(`/new-customer-guide/create-and-login`, {
				customerInfoAsUrlEncodedBase64: encodedCustomer,
				password
			})
			.then(res => {
				const customerToken = res.token
				const encodedIndividualInfo = encodeURIComponent(individualInfoAsBase64)
				history.push(`/ats?auth=${customerToken}&body=${encodedIndividualInfo}`)
			})
			.catch(err => {
				rootStore.clientViewStore.loading = false
				console.error(err)
				setShowErrorMessage(true)
			})
	}

	return (
		<IntlProviderWrapper languageSettings={{ language: customerLanguage }}>
			<div className="new-customer-guide-page">
				<div className="guide-container">
					<div className="inner-guide-container">
						<div className="arrow-wrapper">
							{activeStep !== Steps.Welcome && (
								<IconButton onClick={handleBack}>
									<ArrowBackIosIcon />
								</IconButton>
							)}
						</div>
						{activeStep === Steps.Welcome && <WelcomeSlide />}
						{activeStep === Steps.Info && <InfoSlide />}
						{activeStep === Steps.Auth && <AuthSlide handlePasswordChange={handlePasswordChange} password={password} />}
						{activeStep === Steps.Approval && (
							<ApprovalSlide
								handleClick={createUserAndLogin}
								showErrorMessage={showErrorMessage}
							/>
						)}

						<div className="arrow-wrapper">
							{activeStep !== Steps.Approval && (
								<div className={password.length <= 0 && activeStep === Steps.Auth ? "arrow-wrapper-next-disabled" : "arrow-wrapper-next"}>
									<IconButton disabled={password.length <= 0 && activeStep === Steps.Auth} onClick={handleNext}>
										<ArrowForwardIosIcon />
									</IconButton>
								</div>
							)}
						</div>
					</div>
					<div className="guide-stepper-dots">
						{activeStep === Steps.Welcome ? (
							<RadioButtonCheckedIcon />
						) : (
							<RadioButtonUncheckedIcon />
						)}
						{activeStep === Steps.Info ? (
							<RadioButtonCheckedIcon />
						) : (
							<RadioButtonUncheckedIcon />
						)}
						{activeStep === Steps.Auth ? (
							<RadioButtonCheckedIcon />
						) : (
							<RadioButtonUncheckedIcon />
						)}
						{activeStep === Steps.Approval ? (
							<RadioButtonCheckedIcon />
						) : (
							<RadioButtonUncheckedIcon />
						)}
					</div>
				</div>
				{rootStore.clientViewStore.loading && <Loader />}
			</div>
		</IntlProviderWrapper>
	)
})

const WelcomeSlide = () => {
	const intl = useIntl()

	return (
		<div className="guide-content">
			<Heading1>
				{intl.formatMessage({ id: 'ats.newCustomerGuide.welcomeSlide.title' })}
			</Heading1>
			<div className="guide-body-welcome-slide">
				<div className="welcome-slide-image-wrapper">
					<img src={tfoLogo} />
				</div>
				<div className="welcome-slide-body-container">
					<Paragraph>
						{intl.formatMessage({ id: 'ats.newCustomerGuide.welcomeSlide.body1' })}
					</Paragraph>
					<Paragraph>
						{intl.formatMessage({ id: 'ats.newCustomerGuide.welcomeSlide.body2' })}
					</Paragraph>
				</div>
			</div>
		</div>
	)
}

const InfoSlide = () => {
	const intl = useIntl()

	return (
		<div className="guide-content">
			<Heading1>
				{intl.formatMessage({ id: 'ats.newCustomerGuide.infoSlide.title' })}
			</Heading1>
			<div className="guide-body-info-slide">
				<div className="info-slide-body-container">
					<span className="number-circle">
						<span>1</span>
					</span>
					<Paragraph>
						{intl.formatMessage({ id: 'ats.newCustomerGuide.infoSlide.body1' })}
					</Paragraph>
				</div>

				<div className="info-slide-body-container">
					<span className="number-circle">
						<span>2</span>
					</span>
					<Paragraph>
						{intl.formatMessage({ id: 'ats.newCustomerGuide.infoSlide.body2' })}
					</Paragraph>
				</div>

				<div className="info-slide-body-container">
					<span className="number-circle">
						<span>3</span>
					</span>
					<Paragraph>
						{intl.formatMessage({ id: 'ats.newCustomerGuide.infoSlide.body3' })}
					</Paragraph>
				</div>
			</div>
		</div>
	)
}

const AuthSlide = ({ handlePasswordChange, password }) => {
	const intl = useIntl()

	return (
		<div className="guide-content">
			<Heading1>
				{intl.formatMessage({ id: 'ats.newCustomerGuide.authSlide.title' })}
			</Heading1>
			<div className='guide-body-auth-slide'>
				<div className='auth-slide-body-container'>
					<Paragraph>
						{intl.formatMessage({ id: 'ats.newCustomerGuide.authSlide.body1' })}
					</Paragraph>
					<CreatePasswordFromGuide handlePasswordChange={handlePasswordChange} initPassword={password} />
				</div>
			</div>
		</div>
	)
}

const ApprovalSlide = ({ handleClick, showErrorMessage }) => {
	const intl = useIntl()
	return (
		<div className="guide-content">
			<Heading1>
				{intl.formatMessage({ id: 'ats.newCustomerGuide.approvalSlide.title' })}
			</Heading1>
			<div className="guide-body-approval-slide">
				<Paragraph>
					{intl.formatMessage({ id: 'ats.newCustomerGuide.approvalSlide.body1' })}
				</Paragraph>

				<Paragraph>
					{intl.formatMessage({ id: 'ats.newCustomerGuide.approvalSlide.body2' })}
				</Paragraph>
			</div>

			<div className="guide-button">
				<TextButton
					onClick={() => {
						handleClick()
					}}>
					{intl.formatMessage({ id: 'ats.newCustomerGuide.cta' })}
				</TextButton>
			</div>
			{showErrorMessage && (
				<div className='input-error-container'>
					<InputErrorMessage>
						{intl.formatMessage({ id: `ats.newCustomerGuide.error1` })}
					</InputErrorMessage>
					<InputErrorMessage>
						{intl.formatMessage({ id: `ats.newCustomerGuide.error2` })}
					</InputErrorMessage>
				</div>
			)}

			<Paragraph small>
				{intl.formatMessage(
					{ id: 'ats.newCustomerGuide.disclaimer' },
					{
						link: (
							<a
								href={intl.formatMessage({
									id: 'ats.newCustomerGuide.disclaimer.link',
								})}
								target="_blank"
								rel="noreferrer">
								{intl.formatMessage({
									id: 'ats.newCustomerGuide.disclaimer.linkText',
								})}
							</a>
						),
					},
				)}
			</Paragraph>
		</div>
	)
}
